import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from "react";

import { bemClassNames, getMaxSize, isClose } from "@multiweb/reactUtils";
import { useContentOverflows, useElementScrollData } from "@multiweb/reactUtils/hooks";
import { DropdownLoadingProps, DropdownSizesProps } from "../../types";
import { DropdownArrowDirection, DropdownContainerOverflowArrow } from "../DropdownContainerOverflowArrow";
import { DropdownSkeletonItem } from "../DropdownSkeletonItem";

export type DropdownContainerProps = {
    /** Položky menu */
    children: ReactNode[];
    /** Určuje maximální výšku Dropdownu. */
    maxHeight?: string | number;
    /** Určuje maximální šířku Dropdownu. */
    maxWidth?: string | number;
} & DropdownSizesProps &
    DropdownLoadingProps;

type OverflowState = {
    up: boolean;
    down: boolean;
};

/** Tolerance pri scrollovaní je zde z důvodu toho, že při zoomu může docházet k nepřesnostem v hodnotách. viz. readme */
const SCROLL_TOLERANCE = 1;
/** Minimální šířka dropdownu. */
const MIN_WIDTH = "180px";

const className = bemClassNames("dropdown-container");

const DefaultLoaderRenderer: FunctionComponent<DropdownSizesProps> = ({
    height = "150px",
    maxHeight,
    width = "300px",
    maxWidth,
}) => (
    <div
        className={className("loader")}
        style={{
            height,
            maxHeight,
            width,
            maxWidth,
        }}
    >
        <DropdownSkeletonItem skeletonCount={3} />
    </div>
);

export const DropdownContainer: FunctionComponent<DropdownContainerProps> = ({
    isLoading = false,
    children,
    loaderRenderer = DefaultLoaderRenderer,
    height,
    maxHeight,
    width,
    maxWidth,
}) => {
    const itemsAreaRef = useRef<HTMLDivElement>(null);
    /**
     * Příznak, zda je zobrazeno položek více než je možno zobrazit.
     * Budou zobrazeny šipky
     */
    const hasOverflow = useContentOverflows(itemsAreaRef, false, true);
    const [overflowDirection, setOverflowDirection] = useState<OverflowState>({ up: true, down: false });
    const scrollData = useElementScrollData(itemsAreaRef, [hasOverflow, isLoading]);

    useEffect(() => {
        const { height, scrollHeight, scrollTop } = scrollData;
        // Tato funkce je zde přidána kvůli zoomu, při zoomu dochazelo k tomu, že scrollHeight neodpovidal maximálnímu
        // dostupnému scrollu, ale mohl byt treba 1919.6 nebo 1920.3, takze je treba brat v potaz nejakou toleranci
        const isDownScroll = (): boolean => {
            const currentScroll = Math.round(scrollTop + height);
            return isClose(currentScroll, scrollHeight, SCROLL_TOLERANCE);
        };

        if (hasOverflow) {
            if (scrollTop === 0) setOverflowDirection({ down: true, up: false });
            else if (isDownScroll()) setOverflowDirection({ down: false, up: true });
            else setOverflowDirection({ down: true, up: true });
        }
    }, [hasOverflow, scrollData, isLoading]);

    const calculatedWidth = width ? getMaxSize(width, MIN_WIDTH) : undefined;
    const calculatedMaxWidth = maxWidth ? getMaxSize(maxWidth, MIN_WIDTH) : undefined;
    return (
        <div
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.nativeEvent.stopImmediatePropagation();
                e.stopPropagation();
            }}
            className={className({ hasOverflow, isLoading })}
            style={{ height, maxHeight: `calc(${maxHeight} - var(--base))` }}
        >
            {!isLoading && hasOverflow && (
                <DropdownContainerOverflowArrow direction={DropdownArrowDirection.up} active={overflowDirection.up} />
            )}

            {isLoading && loaderRenderer({ height, maxHeight, width: calculatedWidth, maxWidth: calculatedMaxWidth })}
            {!isLoading && (
                <div
                    ref={itemsAreaRef}
                    className={className("items")}
                    style={{ height, maxWidth: `calc(${calculatedMaxWidth} - var(--base))`, width: calculatedWidth }}
                >
                    {children}
                </div>
            )}

            {!isLoading && hasOverflow && (
                <DropdownContainerOverflowArrow
                    direction={DropdownArrowDirection.down}
                    active={overflowDirection.down}
                />
            )}
        </div>
    );
};
